import { useEffect, useState } from 'react';
import {
  CheckCircle,
  XCircle,
  AlertTriangle,
  Loader2,
  Shield,
  Mail,
  Copy,
  ChevronDown,
  ChevronUp,
  RefreshCw,
  Info,
  Send,
  TrendingDown,
  UserMinus,
  Ban,
} from 'lucide-react';
import { emailApi, type DeliverabilityCheckResult, type DeliverabilityRecordCheck, type DeliverabilityRecommendation, type DeliverabilityStatsResult, type DmarcReportsResult } from '../../lib/api';
import { DnsRecommendations } from './DnsRecommendations';

interface Props {
  domains: string[];
}

// Sekcja "Dostarczalność emaili" — sprawdza SPF/DKIM/DMARC/MTA-STS dla każdej
// domeny z której sklep wysyła maile. Każda domena ma kartę ze score, summary,
// wyjaśnieniem co score oznacza w praktyce i rozwijaną listą rekordów +
// rekomendacji do wklejenia w panelu DNS.
//
// Renderowana wewnątrz EmailStoreModal (zakładka "Dostarczalność") — bez
// zewnętrznego card wrappera, modal ma własny.

export function DeliverabilitySection({ domains }: Props) {
  if (domains.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="inline-flex p-4 rounded-2xl bg-surface-hover/50 mb-4">
          <Mail className="w-8 h-8 text-text-muted" />
        </div>
        <h3 className="text-lg font-semibold text-text-primary mb-2">Brak skrzynek do sprawdzenia</h3>
        <p className="text-sm text-text-secondary max-w-md mx-auto">
          Najpierw skonfiguruj skrzynkę SMTP (zakładka "SMTP") albo IMAP (zakładka "Skrzynka odbiorcza"),
          potem wróć tutaj żeby sprawdzić dostarczalność maili z tych domen.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-start gap-3 p-4 rounded-xl bg-info/10 border border-info/20">
        <Info className="w-5 h-5 text-info flex-shrink-0 mt-0.5" />
        <div className="text-sm text-text-secondary leading-relaxed space-y-2">
          <p>
            <strong className="text-text-primary">Cel kontroli dostarczalności.</strong> Bez poprawnej
            konfiguracji DNS (SPF, DKIM, DMARC) wiadomości transakcyjne często trafiają do folderu spam
            u klientów Gmail / Outlook. Klient nie widzi potwierdzenia zamówienia ani faktury, co generuje
            zgłoszenia do obsługi i pogarsza percepcję marki.
          </p>
          <p>
            <strong className="text-text-primary">Wpływ finansowy.</strong> Brak powiadomienia o nadaniu
            przesyłki (numer śledzenia, link do firmy kurierskiej) zwiększa odsetek nieodebranych paczek.
            Każdy zwrot generuje koszt transportu, magazynowania i często refundacji. Dla sklepu z 500
            zamówieniami miesięcznie pojedyncze procenty nieodebranych przesyłek to tysiące złotych
            miesięcznej straty.
          </p>
          <p>
            Kreator analizuje konfigurację każdej domeny wysyłającej, wskazuje wykryte kanały oraz
            generuje gotowe wartości rekordów do podmiany w panelu DNS dostawcy domeny.
          </p>
        </div>
      </div>

      <div className="space-y-3">
        {domains.map((domain) => (
          <DomainCard key={domain} domain={domain} />
        ))}
      </div>
    </div>
  );
}

function DomainCard({ domain }: { domain: string }) {
  const [rawAuditExpanded, setRawAuditExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<DeliverabilityCheckResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const runCheck = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await emailApi.checkDeliverability(domain);
      setResult(res.data);
    } catch (err: any) {
      setError(err?.response?.data?.error?.message || err?.message || 'Nie udało się sprawdzić');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="border border-border-subtle rounded-xl overflow-hidden">
      <div className="flex items-center justify-between p-4 bg-surface-hover/30">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 rounded-lg bg-surface-hover flex items-center justify-center flex-shrink-0">
            <Shield className="w-5 h-5 text-text-secondary" />
          </div>
          <div className="min-w-0">
            <div className="font-medium text-text-primary truncate">{domain}</div>
            <div className="text-xs text-text-muted mt-0.5">
              Kreator dostarczalności analizuje konfigurację automatycznie
            </div>
          </div>
        </div>
      </div>

      {/* Stats — REALNA sytuacja z ostatnich 30 dni (EmailLog agregacja). */}
      <DomainStats domain={domain} />

      {/* DNS Wizard — kontekstowe rekomendacje z cross-reference do aktywnych
          kanałów wysyłki. Auto-fetch przy mount, default UX. */}
      <DnsRecommendations domain={domain} />

      {/* DMARC reports — agregacja codziennych XML raportów od Google /
          Yahoo / Microsoft. Pojawia się tylko gdy raporty zaczęły przychodzić
          (wymaga rua=mailto:... w DMARC). */}
      <DmarcReportsBlock domain={domain} />

      {/* Surowy audit DNS — dla zaawansowanych użytkowników. Default zwinięty,
          nie zaśmieca głównego widoku. Stary check SPF/DKIM/DMARC/MTA-STS/BIMI
          ze score 0-100. */}
      <div className="border-t border-border-subtle">
        <button
          onClick={() => setRawAuditExpanded((e) => !e)}
          className="w-full flex items-center justify-between px-4 py-3 hover:bg-surface-hover/30 transition-colors text-left"
        >
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-text-secondary">Surowy audit DNS (zaawansowane)</span>
            {result && (
              <span className="text-xs text-text-muted">
                Wynik: {result.score}/100
              </span>
            )}
          </div>
          {rawAuditExpanded ? (
            <ChevronUp className="w-4 h-4 text-text-muted" />
          ) : (
            <ChevronDown className="w-4 h-4 text-text-muted" />
          )}
        </button>

        {rawAuditExpanded && (
          <div className="px-4 py-4 space-y-3 bg-surface-base">
            {!result && !loading && !error && (
              <div className="flex items-center justify-between p-3 rounded-lg bg-surface-hover/30 border border-border-subtle">
                <div className="text-xs text-text-secondary">
                  Pełny check SPF / DKIM / DMARC / MTA-STS / BIMI ze score 0-100.
                </div>
                <button
                  onClick={runCheck}
                  className="px-3 py-1.5 rounded-lg bg-surface-base hover:bg-surface-hover border border-border-subtle text-xs text-text-primary flex items-center gap-1.5"
                >
                  <RefreshCw className="w-3 h-3" />
                  Uruchom audit
                </button>
              </div>
            )}

            {loading && (
              <div className="flex items-center gap-2 text-xs text-text-muted py-3">
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                Odpytuję rekordy DNS...
              </div>
            )}

            {error && (
              <div className="text-xs text-danger py-2">{error}</div>
            )}

            {result && (
              <>
                <div className="flex justify-end">
                  <button
                    onClick={runCheck}
                    disabled={loading}
                    className="px-3 py-1.5 rounded-lg bg-surface-base hover:bg-surface-hover border border-border-subtle text-xs text-text-primary flex items-center gap-1.5 disabled:opacity-50"
                  >
                    <RefreshCw className="w-3 h-3" />
                    Odśwież
                  </button>
                </div>
                <ScoreExplanation score={result.score} />
                <div className="space-y-3">
                  <RecordSection title="SPF" subtitle="Autoryzacja serwerów wysyłających" check={result.spf} />
                  <RecordSection
                    title="DKIM"
                    subtitle="Podpis kryptograficzny maili"
                    check={result.dkim}
                    extra={
                      result.dkim.selectorFound
                        ? `Selektor: ${result.dkim.selectorFound}`
                        : `Sprawdzono ${result.dkim.selectorTried.length} popularnych selektorów`
                    }
                  />
                  <RecordSection title="DMARC" subtitle="Polityka co robić z nieautoryzowanymi mailami" check={result.dmarc} />
                  <RecordSection
                    title="MTA-STS"
                    subtitle="Wymuszenie TLS przy odbiorze (opcjonalne)"
                    check={result.mtaSts}
                    optional
                  />
                  <RecordSection
                    title="BIMI"
                    subtitle="Logo marki obok nadawcy w Gmail / Apple Mail (opcjonalne)"
                    check={result.bimi}
                    optional
                  />
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// Statystyki dostarczalności z ostatnich 30 dni dla domeny. Auto-fetch na
// mount, bez przycisku odświeżania (dane zmieniają się powoli, refetch przy
// otwarciu modalu wystarczy). Empty state gdy 0 maili wysłanych.
function DomainStats({ domain }: { domain: string }) {
  const [stats, setStats] = useState<DeliverabilityStatsResult | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    emailApi
      .getDeliverabilityStats(domain, 30)
      .then((res) => {
        if (!cancelled) setStats(res.data);
      })
      .catch(() => {
        // Ignore — UI pokazuje empty state przy braku danych
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [domain]);

  if (loading) {
    return (
      <div className="px-4 py-3 border-t border-border-subtle bg-surface-base flex items-center gap-2 text-xs text-text-muted">
        <Loader2 className="w-3.5 h-3.5 animate-spin" />
        Ładuję statystyki ostatnich 30 dni...
      </div>
    );
  }

  if (!stats || stats.counts.total === 0) {
    return (
      <div className="px-4 py-3 border-t border-border-subtle bg-surface-base text-xs text-text-muted">
        Brak danych z ostatnich 30 dni. Wyślij pierwsze maile - statystyki pojawią się tutaj automatycznie.
      </div>
    );
  }

  const { counts } = stats;
  const successful = counts.delivered + counts.bouncedHard + counts.bouncedSoft;
  const deliveryRate = successful > 0 ? Math.round((counts.delivered / successful) * 100) : 0;

  return (
    <div className="px-4 py-3 border-t border-border-subtle bg-surface-base">
      <div className="flex items-center justify-between mb-3">
        <div className="text-xs font-medium text-text-secondary">Ostatnie 30 dni</div>
        <div className="text-xs text-text-muted">
          {deliveryRate}% dostarczalności
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        <StatTile
          icon={Send}
          label="Wysłane"
          value={successful}
          color="text-text-primary"
        />
        <StatTile
          icon={CheckCircle}
          label="Dostarczone"
          value={counts.delivered}
          color="text-success"
        />
        <StatTile
          icon={Ban}
          label="Zablokowane"
          value={counts.suppressed}
          color="text-text-secondary"
          tooltip="Próby wysyłki zablokowane przed wysłaniem (adres na liście wypisanych albo hard bounce)"
        />
        <StatTile
          icon={XCircle}
          label="Hard bounce"
          value={counts.bouncedHard}
          color={counts.bouncedHard > 0 ? 'text-danger' : 'text-text-muted'}
          tooltip="Adresy które nie istnieją - automatycznie dodane do listy wypisanych"
        />
        <StatTile
          icon={TrendingDown}
          label="Soft bounce"
          value={counts.bouncedSoft}
          color={counts.bouncedSoft > 0 ? 'text-warning' : 'text-text-muted'}
          tooltip="Przejściowe błędy (skrzynka pełna, serwer odbiorcy down)"
        />
        <StatTile
          icon={UserMinus}
          label="Wypisani"
          value={counts.unsubscribed}
          color={counts.unsubscribed > 0 ? 'text-info' : 'text-text-muted'}
          tooltip="Klienci którzy kliknęli 'Wypisz' w mailu"
        />
      </div>
    </div>
  );
}

function StatTile({
  icon: Icon,
  label,
  value,
  color,
  tooltip,
}: {
  icon: typeof Send;
  label: string;
  value: number;
  color: string;
  tooltip?: string;
}) {
  return (
    <div
      className="flex items-center gap-2 p-2 rounded-lg bg-surface-hover/30 border border-border-subtle"
      title={tooltip}
    >
      <Icon className={`w-4 h-4 flex-shrink-0 ${color}`} />
      <div className="min-w-0">
        <div className={`text-sm font-semibold ${color}`}>{value}</div>
        <div className="text-xs text-text-muted truncate">{label}</div>
      </div>
    </div>
  );
}

// DMARC aggregate reports — sekcja pojawia się tylko gdy reporty
// zaczęły przychodzić. Bez rua=mailto:... w DMARC sekcja jest pusta i
// pokazuje wskazówkę "dodaj rua=mailto: do DMARC żeby tu zobaczyć dane".
function DmarcReportsBlock({ domain }: { domain: string }) {
  const [data, setData] = useState<DmarcReportsResult | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    emailApi
      .getDmarcReports(domain, 30)
      .then((res) => {
        if (!cancelled) setData(res.data);
      })
      .catch(() => {
        // Ignore — UI pokazuje empty state
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [domain]);

  if (loading) return null;
  if (!data || data.reportCount === 0) {
    // Pełny empty state pomijamy dla domen bez raportów - sekcja niech
    // się pojawi dopiero gdy dane wpadną. Krótka info żeby user wiedział
    // o opcji raportowania.
    return (
      <div className="px-4 py-2 border-t border-border-subtle bg-surface-base text-xs text-text-muted">
        Raporty DMARC: brak. Aby otrzymywać codzienne raporty od Google / Yahoo / Microsoft, dodaj{' '}
        <code className="px-1 py-0.5 rounded bg-surface-hover text-text-secondary">rua=mailto:dmarc@{domain}</code>
        {' '}do swojego rekordu DMARC.
      </div>
    );
  }

  const { aggregate, reportCount, topFailSources } = data;

  return (
    <div className="px-4 py-3 border-t border-border-subtle bg-surface-base">
      <div className="flex items-center justify-between mb-3">
        <div className="text-xs font-medium text-text-secondary">
          Raporty DMARC ({reportCount} raportów / 30 dni)
        </div>
        <div className="text-xs text-text-muted">
          {aggregate.alignedPercent}% DMARC-aligned
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2 mb-3">
        <StatTile
          icon={Send}
          label="Maile w raportach"
          value={aggregate.totalMessages}
          color="text-text-primary"
          tooltip="Łączna liczba maili pod Twoją domeną zauważonych przez Google / Yahoo / Microsoft"
        />
        <StatTile
          icon={CheckCircle}
          label="DKIM pass"
          value={aggregate.dkimPassPercent}
          color={aggregate.dkimPassPercent >= 95 ? 'text-success' : aggregate.dkimPassPercent >= 80 ? 'text-warning' : 'text-danger'}
          tooltip={`${aggregate.totalDkimPass} / ${aggregate.totalMessages} maili przeszło DKIM (% w tile)`}
        />
        <StatTile
          icon={CheckCircle}
          label="SPF pass"
          value={aggregate.spfPassPercent}
          color={aggregate.spfPassPercent >= 95 ? 'text-success' : aggregate.spfPassPercent >= 80 ? 'text-warning' : 'text-danger'}
          tooltip={`${aggregate.totalSpfPass} / ${aggregate.totalMessages} maili przeszło SPF (% w tile)`}
        />
      </div>

      {topFailSources.length > 0 && (
        <div className="mt-3 pt-3 border-t border-border-subtle">
          <div className="text-xs font-medium text-text-primary mb-2">
            Top źródła które fail-ują DMARC (potencjalne nieautoryzowane wysyłki)
          </div>
          <div className="space-y-1">
            {topFailSources.map((s) => (
              <div
                key={s.ip}
                className="flex items-center justify-between text-xs px-2 py-1.5 rounded bg-surface-hover/30"
              >
                <code className="text-text-secondary">{s.ip}</code>
                <div className="text-text-muted">
                  <span className="text-danger font-medium">{s.failCount} fail</span>
                  {s.passCount > 0 && <span> / {s.passCount} pass</span>}
                </div>
              </div>
            ))}
          </div>
          <div className="text-xs text-text-muted mt-2 leading-relaxed">
            Sprawdź te IP - jeśli to nie Twoje serwery wysyłające (SMTP / Postmark), ktoś
            podszywa się pod Twoją domenę. DMARC z polityką quarantine albo reject blokuje takie
            wysyłki na poziomie filtrów Gmail / Outlook.
          </div>
        </div>
      )}
    </div>
  );
}

// Wyjaśnienie score po ludzku — co oznacza w praktyce dla biznesu.
// User nie zna terminologii "DKIM/SPF" ale rozumie "co drugi klient nie
// dostanie potwierdzenia zamówienia".
function ScoreExplanation({ score }: { score: number }) {
  const tier =
    score >= 90
      ? 'excellent'
      : score >= 70
      ? 'good'
      : score >= 40
      ? 'poor'
      : 'critical';

  const config = {
    excellent: {
      bg: 'bg-success/10 border-success/30',
      titleColor: 'text-success',
      title: 'Konfiguracja optymalna',
      lines: [
        'Wiadomości docierają do skrzynki głównej w ponad 99% przypadków.',
        'Filtry pocztowe Gmail / Outlook traktują domenę jako zaufaną.',
        'Utrzymanie poziomu wymaga monitorowania raportów DMARC i ostrożnych zmian w konfiguracji.',
      ],
    },
    good: {
      bg: 'bg-info/10 border-info/30',
      titleColor: 'text-info',
      title: 'Konfiguracja akceptowalna',
      lines: [
        'Większość wiadomości dociera, jednak 5-10% trafia do spamu u części odbiorców.',
        'Odbiorcy z agresywnymi filtrami mogą nie zobaczyć wiadomości w głównym folderze.',
        'Zaostrzenie polityki DMARC i dodanie MTA-STS podnosi wynik powyżej 90.',
      ],
    },
    poor: {
      bg: 'bg-warning/10 border-warning/30',
      titleColor: 'text-warning',
      title: 'Konfiguracja niepełna',
      lines: [
        'Znaczna część wiadomości (30-50%) trafia do spamu u odbiorców Gmail / Outlook.',
        'Klienci nie widzą potwierdzeń zamówień ani faktur w głównej skrzynce.',
        'Wymaga uzupełnienia brakujących rekordów DNS. Szczegóły w sekcji "Zalecane zmiany w DNS".',
      ],
    },
    critical: {
      bg: 'bg-danger/10 border-danger/30',
      titleColor: 'text-danger',
      title: 'Konfiguracja krytycznie niepełna',
      lines: [
        'Większość wiadomości (ponad 50%) nie dociera lub trafia do spamu. Filtry pocztowe traktują domenę jako podejrzaną.',
        'Klienci nie otrzymują faktur, potwierdzeń zamówień ani powiadomień o wysyłce.',
        'Wymaga natychmiastowej korekty rekordów DNS. Filtry pocztowe stopniowo obniżają reputację domen bez SPF / DKIM / DMARC.',
      ],
    },
  } as const;

  const c = config[tier];

  return (
    <div className={`p-4 rounded-xl border ${c.bg}`}>
      <div className={`text-sm font-semibold ${c.titleColor} mb-2`}>
        {c.title} ({score}/100)
      </div>
      <div className="space-y-2 text-sm text-text-secondary leading-relaxed">
        {c.lines.map((line, i) => (
          <p key={i}>{line}</p>
        ))}
      </div>
    </div>
  );
}

function RecordSection({
  title,
  subtitle,
  check,
  extra,
  optional,
}: {
  title: string;
  subtitle: string;
  check: DeliverabilityRecordCheck;
  extra?: string;
  optional?: boolean;
}) {
  const status = check.found && check.valid
    ? 'ok'
    : check.found
    ? 'warn'
    : optional
    ? 'optional'
    : 'fail';

  const statusConfig = {
    ok: { Icon: CheckCircle, color: 'text-success', bg: 'bg-success/10' },
    warn: { Icon: AlertTriangle, color: 'text-warning', bg: 'bg-warning/10' },
    fail: { Icon: XCircle, color: 'text-danger', bg: 'bg-danger/10' },
    optional: { Icon: Info, color: 'text-text-muted', bg: 'bg-surface-hover/50' },
  } as const;

  const { Icon, color, bg } = statusConfig[status];

  return (
    <div className={`rounded-lg border border-border-subtle p-4 ${bg}`}>
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="flex items-start gap-3 min-w-0">
          <Icon className={`w-5 h-5 ${color} flex-shrink-0 mt-0.5`} />
          <div className="min-w-0">
            <div className="font-semibold text-text-primary">{title}</div>
            <div className="text-xs text-text-secondary mt-0.5">{subtitle}</div>
            {extra && <div className="text-xs text-text-muted mt-1">{extra}</div>}
          </div>
        </div>
      </div>

      {check.notes.length > 0 && (
        <ul className="text-xs text-text-secondary space-y-1 mt-2 ml-8 list-disc list-inside">
          {check.notes.map((note, i) => (
            <li key={i}>{note}</li>
          ))}
        </ul>
      )}

      {check.value && (
        <div className="mt-3 ml-8">
          <div className="text-xs text-text-muted mb-1">Aktualna wartość:</div>
          <code className="block text-xs text-text-primary bg-surface-base p-2 rounded border border-border-subtle break-all">
            {check.value}
          </code>
        </div>
      )}

      {check.recommendation && <RecommendationBox rec={check.recommendation} />}
    </div>
  );
}

function RecommendationBox({ rec }: { rec: DeliverabilityRecommendation }) {
  const [copiedHost, setCopiedHost] = useState(false);
  const [copiedValue, setCopiedValue] = useState(false);

  const copy = async (text: string, type: 'host' | 'value') => {
    try {
      await navigator.clipboard.writeText(text);
      if (type === 'host') {
        setCopiedHost(true);
        setTimeout(() => setCopiedHost(false), 2000);
      } else {
        setCopiedValue(true);
        setTimeout(() => setCopiedValue(false), 2000);
      }
    } catch {
      // Clipboard API może być niedostępne (HTTP, stare przeglądarki) — fail silently
    }
  };

  return (
    <div className="mt-3 ml-8 border-t border-border-subtle pt-3">
      <div className="text-xs font-medium text-text-primary mb-2">Rekord do dodania w DNS:</div>

      <div className="grid grid-cols-1 sm:grid-cols-[auto_1fr] gap-2 text-xs mb-3">
        <div className="text-text-muted">Typ:</div>
        <code className="bg-surface-base px-2 py-1 rounded border border-border-subtle text-text-primary w-fit">
          {rec.type}
        </code>

        <div className="text-text-muted">Nazwa / Host:</div>
        <div className="flex items-center gap-2">
          <code className="bg-surface-base px-2 py-1 rounded border border-border-subtle text-text-primary break-all flex-1">
            {rec.host}
          </code>
          <button
            onClick={() => copy(rec.host, 'host')}
            className="p-1.5 rounded hover:bg-surface-hover text-text-secondary flex-shrink-0"
            title="Skopiuj"
          >
            {copiedHost ? <CheckCircle className="w-3.5 h-3.5 text-success" /> : <Copy className="w-3.5 h-3.5" />}
          </button>
        </div>

        <div className="text-text-muted">Wartość:</div>
        <div className="flex items-start gap-2">
          <code className="bg-surface-base px-2 py-1 rounded border border-border-subtle text-text-primary break-all flex-1">
            {rec.value}
          </code>
          <button
            onClick={() => copy(rec.value, 'value')}
            className="p-1.5 rounded hover:bg-surface-hover text-text-secondary flex-shrink-0"
            title="Skopiuj wartość"
          >
            {copiedValue ? <CheckCircle className="w-3.5 h-3.5 text-success" /> : <Copy className="w-3.5 h-3.5" />}
          </button>
        </div>
      </div>

      <div className="text-xs text-text-secondary leading-relaxed bg-surface-hover/30 p-3 rounded border border-border-subtle">
        <div className="flex items-start gap-2">
          <Info className="w-3.5 h-3.5 text-info flex-shrink-0 mt-0.5" />
          <div>{rec.instructions}</div>
        </div>
      </div>
    </div>
  );
}
